/**
 * Signing in. Four screens, one task each:
 *
 *   email + password → [choose a password] → [scan a QR] → 6-digit code → in
 *
 * The bracketed screens appear only when the SERVER says they are owed. Every
 * response carries a `status`, and that status is the only thing that decides
 * what comes next — this component never inspects the account itself, which is
 * what the old Firebase flow got wrong (its cached view of "is MFA enrolled"
 * disagreed with the truth, so people were sent back to "choose a password"
 * forever).
 */
import { useEffect, useState } from 'react';
// The browser-safe subpath: @agio/core's index reaches for node:crypto/node:fs.
import { MIN_PASSWORD_LENGTH, type LoginStatus } from '@agio/core/auth';
import { ApiError } from '../../api.js';
import { authApi } from '../../authApi.js';
import { ErrorBanner, SubmitButton } from '../FormBits.js';
import { TotpEnrollStep } from './TotpEnrollStep.js';
import { bigButton, bigInput, codeInput, inputCls, Stepper } from './loginBits.js';

type Screen = 'credentials' | 'newPassword' | 'enroll' | 'code';

const SCREEN_BY_STATUS: Record<Exclude<LoginStatus, 'ok'>, Screen> = {
  password_change_required: 'newPassword',
  totp_enrollment_required: 'enroll',
  totp_required: 'code',
};

export function LoginFlow({ onDone }: { onDone: () => void }) {
  const [screen, setScreen] = useState<Screen | 'loading'>('loading');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  /** One place decides what to show next, from what the server just said. */
  const applyStatus = (status: LoginStatus) => {
    setError(null);
    if (status === 'ok') onDone();
    else setScreen(SCREEN_BY_STATUS[status]);
  };

  /** A reload mid-sign-in resumes where it left off instead of starting over. */
  useEffect(() => {
    authApi
      .session()
      .then(({ status }) => (status ? applyStatus(status) : setScreen('credentials')))
      .catch(() => setScreen('credentials'));
  }, []);

  /** Turns a failed call into either a message or a trip back to the start. */
  const handleFailure = (err: unknown) => {
    if (err instanceof ApiError && err.code === 'session_expired') {
      setScreen('credentials');
      setError(null);
      setNotice('עבר זמן רב מדי — התחברו שוב');
      return;
    }
    setError(err instanceof Error ? err.message : String(err));
  };

  const run = async (call: () => Promise<{ status: LoginStatus }>) => {
    setError(null);
    try {
      applyStatus((await call()).status);
    } catch (err) {
      handleFailure(err);
    }
  };

  if (screen === 'loading') return null;

  if (screen === 'newPassword') {
    return <ChoosePasswordScreen error={error} onSubmit={(pw) => run(() => authApi.setPassword(pw))} />;
  }

  if (screen === 'enroll') {
    return <TotpEnrollStep error={error} onFailure={handleFailure} onConfirm={(code) => run(() => authApi.confirmEnrollment(code))} />;
  }

  if (screen === 'code') {
    return (
      <CodeScreen
        error={error}
        onSubmit={(code) => run(() => authApi.verifyCode(code))}
        onBack={() => {
          setScreen('credentials');
          setError(null);
        }}
      />
    );
  }

  return (
    <CredentialsScreen
      error={error}
      notice={notice}
      onSubmit={(email, password) => run(() => authApi.login(email, password))}
    />
  );
}

// ── screen 1: email + password ───────────────────────────────────────────────

function CredentialsScreen({
  error,
  notice,
  onSubmit,
}: {
  error: string | null;
  notice: string | null;
  onSubmit: (email: string, password: string) => Promise<void>;
}) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);

  return (
    <form
      onSubmit={async (e) => {
        e.preventDefault();
        setBusy(true);
        await onSubmit(email.trim(), password);
        setBusy(false);
      }}
    >
      {notice && <p className="mb-4 rounded-md bg-amber-950/60 p-3 text-sm text-amber-200">{notice}</p>}
      <label className="mb-2 block text-sm text-muted-foreground" htmlFor="login-email">
        אימייל
      </label>
      <input
        id="login-email" dir="ltr" type="email" required autoFocus autoComplete="username" placeholder="you@example.com"
        className={inputCls} value={email} onChange={(e) => setEmail(e.target.value)}
      />
      <label className="mb-2 block text-sm text-muted-foreground" htmlFor="login-password">
        סיסמה
      </label>
      <input
        id="login-password" dir="ltr" type="password" required autoComplete="current-password" placeholder="••••••••"
        className={inputCls} value={password} onChange={(e) => setPassword(e.target.value)}
      />
      <SubmitButton busy={busy} busyLabel="מתחבר…" className="w-full justify-center py-2.5">
        כניסה
      </SubmitButton>
      <ErrorBanner error={error} title="לא הצלחנו להמשיך" />
    </form>
  );
}

// ── screen 2: choose a new password ──────────────────────────────────────────

function ChoosePasswordScreen({ error, onSubmit }: { error: string | null; onSubmit: (pw: string) => Promise<void> }) {
  const [pw1, setPw1] = useState('');
  const [pw2, setPw2] = useState('');
  const [busy, setBusy] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  return (
    <form
      onSubmit={async (e) => {
        e.preventDefault();
        // Checked here only to answer instantly; the server enforces it too.
        if (pw1.length < MIN_PASSWORD_LENGTH) {
          setLocalError(`הסיסמה חייבת להכיל לפחות ${MIN_PASSWORD_LENGTH} תווים`);
          return;
        }
        if (pw1 !== pw2) {
          setLocalError('הסיסמאות אינן זהות');
          return;
        }
        setLocalError(null);
        setBusy(true);
        await onSubmit(pw1);
        setBusy(false);
      }}
    >
      <Stepper current={1} />
      <h2 className="mb-1 text-2xl font-bold">בחרו סיסמה</h2>
      <p className="mb-5 text-base text-white/70">לפחות {MIN_PASSWORD_LENGTH} תווים.</p>
      <label className="mb-2 block text-base" htmlFor="new-password">
        סיסמה חדשה
      </label>
      <input
        id="new-password" dir="ltr" type="password" required autoFocus autoComplete="new-password"
        minLength={MIN_PASSWORD_LENGTH} className={bigInput} value={pw1} onChange={(e) => setPw1(e.target.value)}
      />
      <label className="mb-2 block text-base" htmlFor="new-password-again">
        שוב, לאימות
      </label>
      <input
        id="new-password-again" dir="ltr" type="password" required autoComplete="new-password"
        minLength={MIN_PASSWORD_LENGTH} className={bigInput} value={pw2} onChange={(e) => setPw2(e.target.value)}
      />
      <SubmitButton busy={busy} busyLabel="שומר…" className={bigButton}>
        המשך
      </SubmitButton>
      <ErrorBanner error={localError ?? error} title="לא הצלחנו להמשיך" />
    </form>
  );
}

// ── screen 4: the 6-digit code ───────────────────────────────────────────────

function CodeScreen({
  error,
  onSubmit,
  onBack,
}: {
  error: string | null;
  onSubmit: (code: string) => Promise<void>;
  onBack: () => void;
}) {
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);

  return (
    <form
      onSubmit={async (e) => {
        e.preventDefault();
        setBusy(true);
        await onSubmit(code.trim());
        setBusy(false);
        setCode('');
      }}
    >
      <label className="mb-2 block text-sm text-muted-foreground" htmlFor="totp-code">
        קוד מאפליקציית האימות
      </label>
      <input
        id="totp-code" dir="ltr" required autoFocus inputMode="numeric" pattern="[0-9]{6}" maxLength={6}
        autoComplete="one-time-code" placeholder="123456"
        className={codeInput} value={code} onChange={(e) => setCode(e.target.value)}
      />
      <SubmitButton busy={busy} busyLabel="מאמת…" className={bigButton}>
        אימות
      </SubmitButton>
      <button type="button" className="mt-4 text-xs text-muted-foreground underline" onClick={onBack}>
        חזרה
      </button>
      <ErrorBanner error={error} title="לא הצלחנו להמשיך" />
    </form>
  );
}
